/**
 * Scope-kind helpers for the admin UI. A scope's `kind` (access / domain / data)
 * drives how scopes are grouped and labelled everywhere — the picker sections,
 * the catalog badges, the summary chip.
 *
 * The API is the source of truth for `kind`, but we INFER it from the key as a
 * fallback (same convention the server uses: `Agent.Run`→access, `*.Use`→domain,
 * else data). That keeps the UI correct whether or not the server has started
 * returning `kind` yet — no hard dependency on the backend deploy.
 */
import type { Scope } from './types.js';

export type ScopeKind = 'access' | 'domain' | 'data';

const isScopeKind = (v: unknown): v is ScopeKind =>
  v === 'access' || v === 'domain' || v === 'data';

/** Infer a kind from the scope key alone (the server's convention). */
export const inferScopeKind = (key: string): ScopeKind => {
  if (key === 'Agent.Run') return 'access';
  if (key.endsWith('.Use')) return 'domain';
  return 'data';
};

/** A scope's kind — the catalog value when present, else inferred from the key. */
export const scopeKindOf = (scope: { key: string; kind?: string | null }): ScopeKind =>
  isScopeKind(scope.kind) ? scope.kind : inferScopeKind(scope.key);

export interface ScopeKindMeta {
  kind: ScopeKind;
  /** Section header in the picker / catalog group ("Assistants"). */
  sectionLabel: string;
  /** Plain-language framing beside the section header. */
  hint: string;
  /** Short noun for the summary chip ("access" / "assistant" / "data"). */
  chipNoun: string;
  /** Sort order: Access → Assistants → Data (the permission story). */
  order: number;
  /** Tailwind class for the small kind dot. */
  dotClass: string;
  /** Tailwind classes for a kind badge (soft bg + text + border). */
  badgeClass: string;
}

export const SCOPE_KIND_META: Record<ScopeKind, ScopeKindMeta> = {
  access: {
    kind: 'access',
    sectionLabel: 'Access',
    hint: 'Can they use the assistant at all?',
    chipNoun: 'access',
    order: 0,
    dotClass: 'bg-neutral',
    badgeClass: 'bg-neutral-soft text-neutral border border-neutral-border',
  },
  domain: {
    kind: 'domain',
    sectionLabel: 'Assistants',
    hint: 'Which assistants can they talk to?',
    chipNoun: 'assistant',
    order: 1,
    dotClass: 'bg-info',
    badgeClass: 'bg-info-soft text-info border border-info-border',
  },
  data: {
    kind: 'data',
    sectionLabel: 'Data access',
    hint: 'What can those assistants read for them?',
    chipNoun: 'data',
    order: 2,
    dotClass: 'bg-success',
    badgeClass: 'bg-success-soft text-success border border-success-border',
  },
};

/** All kinds in section order. */
export const orderedScopeKinds = (): ScopeKind[] =>
  (Object.keys(SCOPE_KIND_META) as ScopeKind[]).sort(
    (a, b) => SCOPE_KIND_META[a].order - SCOPE_KIND_META[b].order,
  );

/** Group full scope objects by kind, in section order; empty sections dropped. */
export const groupScopesByKind = (scopes: Scope[]): Array<{ kind: ScopeKind; scopes: Scope[] }> =>
  orderedScopeKinds()
    .map((kind) => ({
      kind,
      scopes: scopes
        .filter((s) => scopeKindOf(s) === kind)
        .sort((a, b) => a.key.localeCompare(b.key)),
    }))
    .filter((g) => g.scopes.length > 0);

/** Count granted scope KEYS by kind (for the summary chip), in section order. */
export const summarizeScopeKinds = (
  keys: readonly string[],
): Array<{ kind: ScopeKind; count: number }> =>
  orderedScopeKinds()
    .map((kind) => ({ kind, count: keys.filter((k) => scopeKindOf({ key: k }) === kind).length }))
    .filter((x) => x.count > 0);

/** "1 assistant" / "2 assistants" — only the assistant noun pluralizes. */
export const scopeKindChipLabel = (kind: ScopeKind, count: number): string => {
  const { chipNoun } = SCOPE_KIND_META[kind];
  const noun = chipNoun === 'assistant' && count !== 1 ? 'assistants' : chipNoun;
  return `${count} ${noun}`;
};
